import { formatMcc } from "@cpos/shared";
import { getBusinessPlan } from "@cpos/shared/src/business/plan";

import { formatCountry } from "./country";

import { connectedAccountUrl } from "@/lib/stripe-links";

import { formatIso, type BusinessProfile } from "../types";
import { buildRoles, parsePerson } from "./person";
import { SidebarField, SidebarSection } from "./SidebarSection";
import { StageBadge } from "./StageBadge";

/**
 * The limit actually in force, flagged when it is a per-merchant override
 * rather than the plan's own — the override is the part worth noticing.
 */
function formatTransactionLimit(business: BusinessProfile): string {
  const cents = getBusinessPlan(business, null).transactionLimit;
  const custom = business.transactionLimitCents != null ? " (custom)" : "";
  return `$${(cents / 100).toFixed(2)}${custom}`;
}

/**
 * The sidebar summary of this merchant: who they are, then what the business
 * is. Everything else — the other users, staff, referral, verification, and
 * the rest of the business record — lives behind "View all".
 *
 * Onboarding status stays here rather than moving wholly into the overlay: it
 * is the one field on the old Verification card that gets scanned rather than
 * looked up, and burying it would cost a glance that people currently get free.
 */
export function ProfileCard({
  business,
  onViewAll,
}: {
  business: BusinessProfile;
  onViewAll: () => void;
}) {
  const isIndividual = business.businessType === "individual";
  const person = parsePerson(
    isIndividual ? business.individual : business.representative,
  );
  const roles = person ? buildRoles(person) : [];

  return (
    <SidebarSection title="Profile" onViewAll={onViewAll}>
      {business.stripeConnectedAccountId && (
        <SidebarField label="Stripe Connected Account">
          <a
            href={connectedAccountUrl(business.stripeConnectedAccountId)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs break-all text-blue-600 hover:text-blue-800 hover:underline"
          >
            {business.stripeConnectedAccountId}
          </a>
        </SidebarField>
      )}

      {person ? (
        <>
          <SidebarField label="Name">
            {person.firstName} {person.lastName}
          </SidebarField>
          {person.email && (
            <SidebarField label="Email">{person.email}</SidebarField>
          )}
          {person.phone && (
            <SidebarField label="Phone">{person.phone}</SidebarField>
          )}
          {!isIndividual && roles.length > 0 && (
            <SidebarField label="Roles">{roles.join(", ")}</SidebarField>
          )}
        </>
      ) : (
        <p className="text-muted-foreground text-sm">No data available.</p>
      )}

      {business.onboardingStatus && (
        <SidebarField label="Onboarding status">
          <StageBadge stage={business.onboardingStatus} />
        </SidebarField>
      )}

      {business.businessType && (
        <SidebarField label="Type">
          {business.businessType.replace(/_/g, " ")}
        </SidebarField>
      )}
      {business.businessCountry && (
        <SidebarField label="Country">
          {formatCountry(business.businessCountry)}
        </SidebarField>
      )}
      {business.merchantCategoryCode && (
        <SidebarField label="Merchant category (MCC)">
          {formatMcc(business.merchantCategoryCode)}
        </SidebarField>
      )}

      <SidebarField label="Transaction limit">
        {formatTransactionLimit(business)}
      </SidebarField>

      {business.createdAt != null && (
        <SidebarField label="Created">
          {formatIso(business.createdAt)}
        </SidebarField>
      )}
    </SidebarSection>
  );
}
